import React, { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import format from 'date-fns/format';
import enUS from 'date-fns/locale/en-US';
import fr from 'date-fns/locale/fr';

import { useToggle } from '@/common/hooks/index';
import styles from '@/common/containers/TaskDetails/styles.less';
import cx from 'classnames';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/common/hooks/index';
import {
  useCreateTaskStepNoteMutation,
  useDeleteTaskStepNoteMutation,
} from '@/entities/task';

const NewNote = ({ loading, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState('');

  const handleSubmit = async () => {
    await onSubmit(noteText);
  };

  return (
    <div className={styles.notesForm}>
      <TextField
        className={cx(styles.notesFormInput)}
        fullWidth
        onChange={({ target: { value } }) => setNoteText(value)}
        value={noteText}
        type="text"
        disabled={loading}
        placeholder={t('Enter your note')}
        multiline
      />
      <div className={styles.notesFormBtns}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          form="addDecedent"
          onClick={handleSubmit}
          disabled={loading || !noteText.length}
          loading={loading}
        >
          {t('Add')}
        </Button>
      </div>
    </div>
  );
};
const Notes = ({ list = [], stepId }) => {
  const { t } = useTranslation();
  const { isFr } = useLocale();
  const [notes, setNotes] = useState(list);
  const [create, toggleCreate] = useToggle(false);
  const [expanded, toggleExpanded] = useToggle(notes.length === 1);
  const dateLocale = isFr ? fr : enUS;
  const createNoteMutation = useCreateTaskStepNoteMutation();
  const deleteNoteMutation = useDeleteTaskStepNoteMutation();
  const loading =
    createNoteMutation.isPending || deleteNoteMutation.isPending;

  const addNote = async (text) => {
    try {
      const data = await createNoteMutation.mutateAsync({
        stepId,
        note: text,
      });
      setNotes([...notes, data]);
      toggleCreate(false);
    } catch (e) {}
  };

  const deleteNote = async (noteId) => {
    await deleteNoteMutation.mutateAsync(noteId);
    setNotes(notes.filter(({ id }) => id !== noteId));
  };

  const handleAccordion = () => {
    toggleExpanded();
  };

  const notesList = useMemo(
    () => (
      <Accordion
        className={styles.notesAccordion}
        expanded={expanded}
        onChange={handleAccordion}
      >
        <AccordionSummary
          expandIcon={expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          {expanded ? t('Hide') : t('Show')} {notes.length} {t('notes')}
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.notesList}>
            {notes.map(({ id, note, createdAt }) => {
              const date = new Date(createdAt);
              return (
                <div key={id} className={styles.notesListItem}>
                  {note.split('\n').map((row, ind) => (
                    <p key={ind}>{row}</p>
                  ))}
                  <span>
                    {format(date, 'dd MMM yyyy', { locale: dateLocale })}
                    {' | '}
                    {format(date, "hh:mm aaaaa'm'")}
                  </span>
                  <div className={styles.notesListItemDelete}>
                    <DeleteIcon onClick={() => !loading && deleteNote(id)} />
                  </div>
                </div>
              );
            })}
          </div>
        </AccordionDetails>
      </Accordion>
    ),
    [loading, notes, deleteNote]
  );

  return (
    <div className={styles.notes}>
      {notes.length > 0 && notesList}
      {!create ? (
        <div
          className={cx(styles.notesForm, 'linkBack')}
          onClick={toggleCreate}
        >
          <AddIcon /> {t('Add note')}
        </div>
      ) : (
        <NewNote onSubmit={addNote} loading={loading} onCancel={toggleCreate} />
      )}
    </div>
  );
};

export default Notes;
